import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/util';

export function useCompany(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId],
    queryFn: () =>
      apiClient.companies.companiesRetrieve({
        id: companyId
      }),
    ...options
  });
}

export function useCompanyContact(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'contact'],
    queryFn: () =>
      apiClient.companies.companiesContactDataRetrieve({
        id: companyId
      }),
    ...options
  });
}

export function useCompanyGeneralData(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'generalData'],
    queryFn: () =>
      apiClient.companies.companiesGeneralDataRetrieve({
        id: companyId
      }),
    ...options
  });
}

export const useCompanySettings = (companyId: string, options = {}) => {
  return useQuery({
    queryKey: ['company', companyId, 'settings'],
    queryFn: () =>
      apiClient.companies.companiesSettingsRetrieve({
        id: companyId
      }),
    enabled: !!companyId,
    ...options
  });
};

export function useCompanyBenefitSettingsData(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'benefitSettings'],
    queryFn: () =>
      apiClient.companies.companiesBenefitSettingsRetrieve({
        id: companyId
      }),
    ...options
  });
}

export function useHrisData(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'hrisData'],
    queryFn: () =>
      apiClient.companies.companiesHrisList({
        parentCompany: companyId
      }),
    ...options
  });
}

export const useHrisSetupStatus = (companyId: string, options = {}) => {
  return useQuery({
    queryKey: ['company', companyId, 'hris', 'setup-status'],
    queryFn: () =>
      apiClient.companies.companiesHrisHrisSetupStatusRetrieve({
        parentCompany: companyId
      }),
    ...options
  });
};

export function usePaymentData(companyId?: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'paymentData'],
    queryFn: () =>
      apiClient.companies.companiesPaymentDataRetrieve({
        id: companyId!
      }),
    enabled: !!companyId,
    staleTime: 60 * 1000,
    ...options
  });
}

export function useCompanySettingsPayroll(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'settings', 'payroll'],
    queryFn: () =>
      apiClient.companies.companiesSettingsPayrollRetrieve({
        id: companyId
      }),
    ...options
  });
}

export function useCompanyLocations(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'locations'],
    queryFn: () =>
      apiClient.locations.companiesLocationsList({
        parentCompany: companyId
      }),
    enabled: !!companyId,
    ...options
  });
}

export function useCompanyDepartments(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'departments'],
    queryFn: () =>
      apiClient.departments.companiesDepartmentsList({
        parentCompany: companyId
      }),
    enabled: !!companyId,
    ...options
  });
}

export function useCompanyCostCenters(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'costCenters'],
    queryFn: () =>
      apiClient.costCenters.companiesCostCentersList({
        parentCompany: companyId
      }),
    enabled: !!companyId,
    ...options
  });
}

export function useCompanyGroups(companyId: string, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'groups'],
    queryFn: () =>
      apiClient.groups.companiesGroupsList({
        parentCompany: companyId
      }),
    ...options
  });
}

export const useCompanyContactData = (companyId: string) => {
  return useQuery({
    queryKey: ['companyContactData', companyId],
    queryFn: () =>
      apiClient.companies.companiesContactDataRetrieve({
        id: companyId
      })
  });
};

export const useCompanyReferralLink = (companyId: string, options = {}) =>
  useQuery({
    queryKey: ['company', companyId, 'referralLink'],
    queryFn: () => apiClient.companies.companiesReferralLinkRetrieve({ id: companyId }),
    enabled: !!companyId,
    ...options
  });
